import { zodResolver } from '@hookform/resolvers/zod'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { Mail, Phone, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import type { Path } from 'react-hook-form'
import { Banner, Button, Drawer, PasswordField, TextField } from '@shared/design-system/ui'
import { AppError } from '@shared/api/error'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { formatPhone } from '@shared/lib/format/masks'
import { useSession } from '@shared/lib/auth/session-store'
import { toast } from '@shared/lib/toast/toast-store'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { useQueryClient } from '@tanstack/react-query'
import { currentUserQuery } from '@entities/user'
import { RegisterInputSchema } from '../api/auth.contract'
import { useRegisterMutation, useSignupTerms } from '../api/auth.mutations'
import type { RegisterInput } from '../api/auth.contract'
import { getPasswordStrength } from '../model/password-strength'
import { navigateTo } from './auth-routing'
import {
  actions,
  consentCheckbox,
  consentLine,
  form,
  inline,
  link,
  legalCopy,
  mutedText,
} from './auth.css'
import { AuthLayout } from './AuthLayout'

export function RegisterPage() {
  const referralProgram = useFeatureFlag('referralProgram')
  const [termsOpen, setTermsOpen] = useState(false)
  const queryClient = useQueryClient()
  const setSession = useSession((state) => state.setSession)
  const setUser = useSession((state) => state.setUser)
  const clearSession = useSession((state) => state.clear)
  const registerMutation = useRegisterMutation()
  const terms = useSignupTerms()
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterInput>({
    resolver: zodResolver(RegisterInputSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
      termsVersion: '',
    },
  })
  useEffect(() => {
    if (!terms.data?.version) return
    setValue('termsVersion', terms.data.version)
    setValue('termsAccepted', false)
    clearErrors(['termsVersion', 'termsAccepted'])
  }, [terms.data?.version, setValue, clearErrors])
  const password = useWatch({ control, name: 'password' }) ?? ''
  const phoneField = register('phone')

  const onSubmit = async (values: RegisterInput) => {
    try {
      const referralToken = new URLSearchParams(window.location.search).get('ref')
      const response = await registerMutation.mutateAsync({
        ...values,
        ...(referralProgram && referralToken && /^[A-Za-z0-9_-]{32}$/.test(referralToken)
          ? { referralToken }
          : {}),
      })
      setSession(response.token, response.user)
      try {
        setUser(await queryClient.fetchQuery({ ...currentUserQuery(), staleTime: 0 }))
      } catch (error) {
        clearSession()
        throw error
      }
      toast.success('Conta criada. Vamos concluir seu cadastro.')
      navigateTo('/onboarding')
    } catch (error) {
      if (error instanceof AppError && error.code === 'StaleTermsVersionError') {
        // Loading the new version clears the acceptance, so the seller accepts it again.
        void terms.refetch()
        toast.error(getUserFacingError(error))
        return
      }
      const hasFieldErrors = applyApiFieldErrors(error, (field, fieldError) =>
        setError(field as Path<RegisterInput>, fieldError),
      )
      if (hasFieldErrors) {
        toast.error({
          title: 'Revise os campos',
          description: 'Confira os campos destacados antes de criar a conta.',
        })
        return
      }

      if (error instanceof AppError && error.code === 'EmailAlreadyRegisteredError') {
        setError('email', { type: 'server', message: 'Este e-mail já está cadastrado.' })
        toast.error({
          title: 'E-mail já cadastrado',
          description: 'Use outro e-mail ou entre na sua conta.',
        })
        return
      }

      if (error instanceof AppError && error.code === 'PhoneAlreadyRegisteredError') {
        setError('phone', {
          type: 'server',
          message: 'Este telefone já está cadastrado em outra conta.',
        })
        toast.error({
          title: 'Telefone já cadastrado',
          description: 'Use outro telefone para criar a conta.',
        })
        return
      }

      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível criar a conta',
          fallback: 'Confira seus dados e tente novamente.',
        }),
      )
    }
  }

  return (
    <AuthLayout
      title="Criar conta"
      subtitle="Informe seus dados de acesso para iniciar o cadastro da VendePay."
      wide
    >
      <form
        data-testid="register-form"
        className={form}
        onSubmit={(event) => void handleSubmit(onSubmit)(event)}
      >
        {errors.confirmPassword ? (
          <Banner tone="danger" title="Revise os campos">
            Confira a confirmação da senha antes de criar a conta.
          </Banner>
        ) : null}
        <TextField
          label="Nome completo"
          autoComplete="name"
          leftIcon={<UserRound size={17} />}
          error={errors.name?.message}
          {...register('name')}
        />
        <TextField
          label="E-mail"
          type="email"
          autoComplete="email"
          leftIcon={<Mail size={17} />}
          error={errors.email?.message}
          {...register('email')}
        />
        <TextField
          label="Telefone"
          autoComplete="tel"
          inputMode="tel"
          helperText="Opcional"
          leftIcon={<Phone size={17} />}
          error={errors.phone?.message}
          {...phoneField}
          onChange={(event) => {
            setValue('phone', formatPhone(event.target.value), { shouldValidate: true })
          }}
        />
        <PasswordField
          label="Senha"
          autoComplete="new-password"
          error={errors.password?.message}
          strength={getPasswordStrength(password)}
          {...register('password')}
        />
        <PasswordField
          label="Confirmar senha"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <div>
          <div className={consentLine}>
            <input
              className={consentCheckbox}
              type="checkbox"
              disabled={!terms.data}
              aria-label={terms.data?.consentLabel ?? 'Aceite dos termos'}
              {...register('termsAccepted')}
            />
            {terms.data ? (
              <span>
                Li e aceito os{' '}
                <button className={link} type="button" onClick={() => setTermsOpen(true)}>
                  Termos de Uso
                </button>{' '}
                e a{' '}
                <a
                  className={link}
                  href={terms.data.vendepayPrivacyUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Política de Privacidade
                </a>{' '}
                da VendePay e o{' '}
                <a
                  className={link}
                  href={terms.data.stripeAgreementUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Contrato de Conta Conectada da Stripe
                </a>
                .
              </span>
            ) : (
              <span>Carregando os termos de cadastro...</span>
            )}
          </div>
          {errors.termsAccepted ? <p role="alert">{errors.termsAccepted.message}</p> : null}
        </div>
        <div className={actions}>
          <Button type="submit" fullWidth disabled={registerMutation.isPending || !terms.data}>
            {registerMutation.isPending ? 'Criando...' : 'Criar conta'}
          </Button>
          <div className={inline}>
            <span className={mutedText}>Já tem conta?</span>
            <a className={link} href="/login">
              Entrar
            </a>
          </div>
        </div>
      </form>
      <Drawer
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        title="Termos de Uso"
        description="Leia o texto integral aplicável ao cadastro."
        size="standard"
        footer={
          <Button
            onClick={() => {
              setValue('termsAccepted', true, { shouldValidate: true })
              setTermsOpen(false)
            }}
          >
            Li e aceito
          </Button>
        }
      >
        {terms.data ? (
          <div className={legalCopy}>
            <p>{terms.data.consentText}</p>
            <p>
              <a
                className={link}
                href={terms.data.vendepayTermsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Termos Gerais de Uso da VendePay
              </a>
              {' · '}
              <a
                className={link}
                href={terms.data.vendepayPrivacyUrl}
                target="_blank"
                rel="noreferrer"
              >
                Privacidade da VendePay
              </a>
              {' · '}
              <a
                className={link}
                href={terms.data.stripeAgreementUrl}
                target="_blank"
                rel="noreferrer"
              >
                Contrato de Conta Conectada da Stripe
              </a>
              {' · '}
              <a
                className={link}
                href={terms.data.stripeServicesUrl}
                target="_blank"
                rel="noreferrer"
              >
                Contrato de Serviços da Stripe
              </a>
              {' · '}
              <a
                className={link}
                href={terms.data.stripePrivacyUrl}
                target="_blank"
                rel="noreferrer"
              >
                Privacidade da Stripe
              </a>
              .
            </p>
            <p>{terms.data.acquirerDisclosure}</p>
          </div>
        ) : null}
      </Drawer>
    </AuthLayout>
  )
}
